/**
 * Ядро дизайн-системы: то, что доказуемо одинаково во всех трёх зрелых
 * приложениях (AF-23).
 *
 * ─── Почему здесь 18 значений, а не вся тема ────────────────────────────────
 *
 * У deals, applications и analyst по пять одноимённых экспортов темы —
 * `palette`, `lightPalette`, `borders`, `shadows`, `shape`. Имя общее,
 * содержимое нет: в них 79 ключей, у всех трёх присутствует 21, и совпадают
 * значения у 18.
 *
 *     экспорт        ключей  у всех трёх  и совпадают
 *     palette            42        12          11
 *     lightPalette       22         1           0
 *     borders             3         1           0
 *     shadows             6         4           4
 *     shape               6         3           3
 *     ИТОГО              79        21          18
 *
 * Расходятся не столько значения, сколько раскладка: у deals `background.*` и
 * `border.*` лежат внутри `palette`, у двух других — в `lightPalette`. Плюс
 * своё у каждого: `cyan` у analyst, `errorFocusRing` у analyst,
 * `landingRadius` и `smallRadius` у двоих.
 *
 * Поэтому подъём «всех пяти экспортов» перенёс бы 79 ключей, из которых 61
 * общими не являются. Сведение раскладки — отдельная работа (AF-43), и она
 * снова меняет вид, то есть идёт через владельца.
 *
 * ─── Что это за 18 ──────────────────────────────────────────────────────────
 *
 * Не случайный остаток: семантическая палитра, шкала теней и радиусы — то, из
 * чего дизайн-система и состоит. И именно в этом наборе все девять пар
 * «текст на подложке» проходят WCAG AA: он результат AF-23, а не того, что
 * уцелело.
 *
 *     error   #C62828 на #FDF3F3  5.16:1
 *     success #2E7D32 на #F0F7F0  4.70:1
 *     warning #C2410C на #FEFCE8  5.01:1
 *
 * ─── Чего здесь намеренно НЕТ ───────────────────────────────────────────────
 *
 * `secondary.main` — единственный ключ, который есть у всех троих и
 * расходится: `#757575` у deals и analyst против `#F291A3` у applications, и
 * над ним в `applications/src/theme/tokens.ts` написано «Розовый акцент
 * проекта». Это вопрос идентичности, а не расхождение: порога, который бы
 * рассудил серый против розового, не существует. Сводить его по большинству
 * голосов значило бы стереть фирменный цвет под видом унификации.
 *
 * `error.dark`, `error.contrastText` и то же у `success`/`warning` есть только
 * у deals — у соседей эти семейства из двух ключей. Приложение, которому они
 * нужны, дописывает их у себя поверх этих.
 */

/**
 * Цвета дизайн-системы, разложенные ПО РЕЖИМАМ.
 *
 * ─── Почему не плоским набором ──────────────────────────────────────────────
 *
 * Первая версия этого файла экспортировала одиннадцать значений как `colors` —
 * просто «цвета дизайн-системы». Это было бы неверно, и неверно молча: все
 * одиннадцать осмысленны на белом. `error.light` `#FDF3F3` — почти белый, он
 * подложка под тёмный текст; в тёмной теме это светлое пятно, а не подложка.
 * То есть по смыслу они `light.*`, а имя говорило бы «цвета вообще».
 *
 * Прибивает переключение тем не значение, а форма: **отсутствие измерения
 * «режим» в самой структуре**. Завести тёмную половину потом, не переделывая
 * экспорт, стало бы нельзя, а переделывать пришлось бы уже во всех
 * приложениях-потребителях.
 *
 * Форма выбрана под то, как приложения уже собирают тему: все три
 * `ThemeManager` работают с `cssVariables: true` и `mode`, то есть MUI-шные
 * `colorSchemes` подключаются сюда напрямую.
 *
 * ─── Про пустую тёмную половину ─────────────────────────────────────────────
 *
 * `dark` пуста, и это ФАКТ, а не заготовка. Сегодня тёмную тему во всех трёх
 * приложениях выводит MUI сам — комментарий в `analyst/.../ThemeManager.tsx`
 * говорит это прямо: «Dark mode: MUI автоматически подбирает цвета». Никто не
 * проверял, что получается. У deals `darkPalette` нет вовсе (замер: 0
 * упоминаний против 1 у applications и 1 у analyst).
 *
 * Пустой объект здесь честнее отсутствия ключа: он показывает, что измерение
 * есть, а половина не заполнена. Заполнение — отдельная задача и отдельное
 * решение владельца, потому что это видимое изменение продукта.
 */
export const colorSchemes = {
  light: {
    /**
     * `contrastText` здесь НЕ объявлен, и это решение, а не пропуск (AF-44).
     *
     * MUI считает его сам: `augmentColor` вызывает `getContrastText(main)` —
     * но только `if (!color.contrastText)`. Прибитое значение подавляет
     * штатный механизм, и тогда при смене `main` цвет текста за ним не едет.
     * Ровно это и случилось: пока `warning.main` был `#E65100`, прибитый белый
     * давал 3.79:1 при пороге AA 4.5, и об этом никто не узнал.
     *
     * Порог задаётся в теме приложения: `contrastThreshold: 4.5`. Дефолт MUI —
     * 3, и его недостаточно, см. комментарий там.
     */
    primary: {
      main: '#3A558C',
      light: '#4A6AAF',
      dark: '#2C4270',
      subtle: '#F5F5F5',
    },
    /**
     * `light` во всех трёх семействах — БЛЕДНАЯ ПОДЛОЖКА под текст цвета
     * `main`, а не насыщенный оттенок. Разница не косметическая: до AF-23 у
     * deals это имя значило обратное, и пара `main` на `light` давала 1.61:1
     * при пороге 4.5:1. Компоненту, которому нужна насыщенная заливка, брать
     * `main`.
     *
     * Обратите внимание на вложенность имён: `colorSchemes.light.error.light`.
     * Внешний `light` — режим, внутренний — роль внутри семейства. Совпадение
     * неудачное, но оба имени пришли не отсюда: внешнее задаёт MUI, внутреннее
     *живёт в приложениях с самого начала.
     */
    error: {
      main: '#C62828',
      light: '#FDF3F3',
    },
    success: {
      main: '#2E7D32',
      light: '#F0F7F0',
    },
    warning: {
      main: '#C2410C',
      light: '#FEFCE8',
    },
  },

  /**
   * Не заполнена — см. выше. Тип намеренно пустой: обратиться к
   * `colorSchemes.dark.primary` не даст компилятор, и это правильнее, чем
   * отдать значение, которого никто не выбирал.
   */
  dark: {},
} as const;

/**
 * Тени. Swiss-стиль: минимальные, предпочтение границам.
 *
 * `lg` и `errorFocusRing` сюда не входят — их нет у applications.
 *
 * По режимам НЕ разложены, и это описание сегодняшнего состояния, а не
 * утверждение, что они от режима не зависят. Все три приложения сейчас
 * используют один набор в обеих темах. При этом `rgba(0, 0, 0, …)` — заведомо
 * светлое допущение: на тёмном фоне чёрная тень невидима. Когда тёмная
 * половина `colorSchemes` будет заполнена, сюда придётся вернуться.
 */
export const shadows = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.02)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.03)',
  md: '0 2px 8px rgba(0, 0, 0, 0.04)',
  focusRing: '0 0 0 3px rgba(58, 85, 140, 0.12)',
} as const;

/**
 * Радиусы. От режима не зависят по существу, а не по недосмотру: геометрия
 * одинакова в светлой и тёмной теме.
 *
 * Имя `radii`, а не `shape`: у приложений в `shape` лежат ещё
 * `smallRadius` и `landingRadius`, которых нет у applications, — то есть
 * `shape` приложения шире этого набора и совпадением имён вводил бы в
 * заблуждение.
 */
export const radii = {
  borderRadius: 4,
  buttonRadius: 4,
  inputRadius: 4,
} as const;

/**
 * Семейства шрифтов (AF-577).
 *
 * ─── Почему стек живёт здесь, а не в приложении ─────────────────────────────
 *
 * До 27.09.2026 моноширинный стек был задан в ПЯТИ местах тремя разными
 * значениями:
 *
 *     apps/deals/src/theme/tokens.ts         "JetBrains Mono", "Fira Code", "Consolas", monospace
 *     apps/applications/src/theme/tokens.ts  "JetBrains Mono", "DM Mono", monospace
 *     apps/analyst/src/theme/tokens.ts       "JetBrains Mono", "DM Mono", monospace
 *     apps/applications/.../AuthPageTitle    "JetBrains Mono", monospace   ← мимо своего же токена
 *     apps/analyst/.../AuthPageTitle         "JetBrains Mono", monospace   ← мимо своего же токена
 *
 * Последние два — не опечатка, а закономерность: пока стек можно написать
 * строкой, его пишут строкой. Общий компонент шапки обязан брать стек отсюда,
 * иначе пакет добавил бы к пяти адресам шестой.
 *
 * ─── Почему именно этот стек ────────────────────────────────────────────────
 *
 * Взят вариант deals — он эталон оформления по решению владельца (27.09.2026),
 * и он же единственный из трёх, у которого фолбэк осмыслен: `Fira Code` и
 * `Consolas` — шрифты той же ширины (advance 0.6em), поэтому подмена не двигает
 * вёрстку. `DM Mono` у applications и analyst в проект не подключён ни одним
 * `@fontsource`-импортом (замер: `grep -rn 'dm-mono'` по `apps`, каталог
 * приложения плюс `src` — 0 совпадений; шаблон записан словами, потому что
 * звёздочка со слэшем закрыла бы этот комментарий досрочно —
 * measurement-traps, пункт 24), то есть
 * это имя без шрифта, и стек из трёх элементов работал как стек из двух.
 *
 * ─── Чего этот токен НЕ решает ──────────────────────────────────────────────
 *
 * Стек называет шрифт, но не подключает его. Набор ГРАНЕЙ задаётся
 * `@fontsource`-импортами в точке входа приложения, и они расходятся отдельно
 * (AF-578).
 */
export const fontFamily = {
  mono: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
} as const;
